import { Camera } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  OBJECT_DETECTION_ICONS,
  AUDIO_DETECTION_ICONS,
  DEFAULT_OBJECT_ICON,
  DEFAULT_AUDIO_ICON,
} from '@/config/detectionIcons'
import { cn } from '@/lib/utils'
import type { AudioDetection, VisualDetection } from '@/types'

// ── Types ───────────────────────────────────────────────────────────────────

export type NotificationVariant = 'alert' | 'standard'

export interface DetectionNotification {
  id: string
  variant: NotificationVariant
  event: any
  eventType: string
  title: string
  camera: string | undefined
  cameraGroup: string | undefined
  confidence: number | undefined
  thumbnailUrl: string | undefined
  timestamp: string | null
  detectedObjects: string[]
  audioDetections: AudioDetection[]
  detectionMap: Record<string, number>
  isRead: boolean
  createdAt: number
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export function confClass(c: number) {
  if (c >= 0.8) return 'det-conf-high'
  if (c >= 0.5) return 'det-conf-med'
  return 'det-conf-low'
}

export function formatTimestamp(ts: string | undefined): string | null {
  if (!ts) return null
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
  } catch { return null }
}

export function parseEvent(event: any): Omit<DetectionNotification, 'id' | 'variant' | 'isRead' | 'createdAt'> {
  const eventType = event?.event_type as string ?? 'detection'
  // Store raw eventType for render-time translation; title is a static fallback
  const title = eventType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
  const camera          = event?.camera_name as string | undefined
  const cameraGroup     = (event?.camera_group ?? event?.group_name) as string | undefined
  const confidence      = event?.confidence_score as number | undefined
  const thumbnailUrl    = event?.thumbnail_url as string | undefined
  const timestamp       = formatTimestamp(event?.timestamp ?? event?.created_at)
  const detectedObjects = (event?.detected_objects as string[]) ?? []
  const audioDetections = (event?.audio_detections as AudioDetection[]) ?? []
  const detectionMap: Record<string, number> = {}
  const metaDetections  = (event?.metadata?.detections as VisualDetection[]) ?? []
  // Max confidence per class across frames (best detection), not the last-frame value. (#095 follow-up)
  for (const d of metaDetections) detectionMap[d.class_name] = Math.max(detectionMap[d.class_name] ?? 0, d.confidence)
  return { event, eventType, title, camera, cameraGroup, confidence, thumbnailUrl, timestamp, detectedObjects, audioDetections, detectionMap }
}

// ── Audio beep ──────────────────────────────────────────────────────────────

export function playDetectionSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
    const now = ctx.currentTime
    const freqs = [880, 1100]
    freqs.forEach((freq, i) => {
      const osc  = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.type = 'sine'
      osc.frequency.value = freq
      const start = now + i * 0.12
      gain.gain.setValueAtTime(0, start)
      gain.gain.linearRampToValueAtTime(0.15, start + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.1)
      osc.start(start)
      osc.stop(start + 0.12)
    })
    setTimeout(() => ctx.close(), 500)
  } catch {
    // AudioContext not available
  }
}

// ── Shared sub-components ───────────────────────────────────────────────────

export function DetectionChips({ objects, detectionMap }: {
  objects: string[]
  detectionMap: Record<string, number>
}) {
  const { t } = useTranslation('common')
  if (objects.length === 0) return null
  return (
    <div className="det-chips">
      {[...new Set(objects)].map((cls) => {
        const cfg   = OBJECT_DETECTION_ICONS[cls] ?? DEFAULT_OBJECT_ICON
        const Icon  = cfg.icon
        const conf  = detectionMap[cls]
        const label = t(`detectionClasses.${cls}`, { defaultValue: cfg.label || cls.replace(/_/g, ' ') })
        return (
          <span key={cls} className="det-chip" title={conf !== undefined ? `${label} (${Math.round(conf * 100)}%)` : label}>
            <Icon className={cn('det-chip-icon', cfg.color)} weight="duotone" />
            <span>{label}</span>
            {conf !== undefined && <span className="det-chip-conf">{Math.round(conf * 100)}%</span>}
          </span>
        )
      })}
    </div>
  )
}

export function AudioChips({ detections }: { detections: AudioDetection[] }) {
  const { t } = useTranslation('common')
  if (detections.length === 0) return null
  return (
    <div className="det-chips">
      <span className="det-audio-label">{t('audioClasses._label', { defaultValue: 'Audio' })}</span>
      {detections.map((d) => {
        const cfg   = AUDIO_DETECTION_ICONS[d.class_name] ?? DEFAULT_AUDIO_ICON
        const Icon  = cfg.icon
        const label = t(`audioClasses.${d.class_name}`, { defaultValue: cfg.label || d.class_name.replace(/_/g, ' ') })
        return (
          <span key={d.class_name} className="det-chip" title={`${label} (${Math.round(d.confidence * 100)}%)`}>
            <Icon className={cn('det-chip-icon', cfg.color)} weight="duotone" />
            <span>{label}</span>
            <span className="det-chip-conf">{Math.round(d.confidence * 100)}%</span>
          </span>
        )
      })}
    </div>
  )
}

export function CameraInfo({ camera, group, small }: { camera?: string; group?: string; small?: boolean }) {
  if (!camera) return null
  return (
    <p className={cn('det-camera-name', small && 'det-camera-name-sm')}>
      <Camera className="det-camera-icon" />
      <span>{camera}</span>
      {group && <span className="det-group-name">· {group}</span>}
    </p>
  )
}
